import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getCrops } from '@next/arable-lib/crop';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CropField, ImportCropfieldsSelection, SiteState } from 'src/app/shared/models';

import { LoadCropVarietiesByCropIdsRequest } from '../../store/actions/crop-variety.actions';
import { getCropVarietiesByCropId } from '../../store/selectors/crop-variety.selectors';

export interface ImportCropfieldsDialogModel extends AbstractFormDialogData {
  cropFields: CropField[];
  year?: number;
}

export interface SelectableCropField extends CropField {
  isSelected: boolean;
  cropName: string;
  cropVarietyName: string;
}

@Component({
  selector: 'app-import-cropfields-dialog',
  templateUrl: './import-cropfields-dialog.component.html',
  styleUrls: ['./import-cropfields-dialog.component.scss'],
  standalone: false,
})
export class ImportCropfieldsDialogComponent
  extends AbstractFormDialog<ImportCropfieldsSelection>
  implements OnInit, OnDestroy
{
  data = inject<ImportCropfieldsDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        ImportCropfieldsDialogComponent,
        AbstractDialogResultData<ImportCropfieldsSelection>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  formGroup = this.fb.group({ year: [this.data.year, [Validators.required]] });

  selectableCropFields: SelectableCropField[] = [];

  ngOnInit(): void {
    const cropIds = this.data.cropFields
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);
    setTimeout(() => this.store.dispatch(new LoadCropVarietiesByCropIdsRequest(cropIds)), 0);

    this.selectableCropFields = [...this.data.cropFields]
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(
        (cropField) =>
          ({
            ...cropField,
            isSelected: false,
            cropName: null,
            cropVarietyName: null,
          }) as SelectableCropField,
      );

    this.store.pipe(select(getCrops), takeUntil(this.destroyed$)).subscribe((crops) => {
      for (const cropField of this.selectableCropFields) {
        cropField.cropName = crops.find((crop) => crop.id === cropField.cropId)?.name;
      }
    });

    this.store
      .pipe(select(getCropVarietiesByCropId), takeUntil(this.destroyed$))
      .subscribe((cropVarietiesByCropId) => {
        for (const cropField of this.selectableCropFields) {
          const cropVarieties = cropVarietiesByCropId.find(
            (cropVariety) => cropVariety.id === cropField.cropId,
          )?.cropVarieties;
          if (cropVarieties) {
            cropField.cropVarietyName = cropVarieties.find(
              (cropVariety) => cropVariety.id === cropField.cropVarietyId,
            )?.name;
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  selectCropField(selectableCropField: SelectableCropField) {
    selectableCropField.isSelected = !selectableCropField.isSelected;
  }

  areAllCropFieldSelected = (): boolean =>
    this.selectableCropFields.every((cropField) => cropField.isSelected);

  toggleSelection() {
    const areAllCropFieldSelected = this.areAllCropFieldSelected();
    for (const cropField of this.selectableCropFields) {
      cropField.isSelected = !areAllCropFieldSelected;
    }
  }

  confirmSelection(result: ImportCropfieldsSelection) {
    result.cropFields = this.selectableCropFields
      .filter((f) => f.isSelected)
      .map((f) => f as CropField);
    this.confirm(result);
  }

  getCropNamesFromSelectableCropFieldsWithCropFieldCounts = (): { [key: string]: number } =>
    this.selectableCropFields.reduce((acc: { [key: string]: number }, obj: SelectableCropField) => {
      const key = obj.cropName;
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key]++;
      return acc;
    }, {});

  toggleAllCropFieldsWithCropName(cropName: string) {
    const isSelected = this.selectableCropFields.some(
      (cropField) => cropField.cropName === cropName && cropField.isSelected,
    );

    this.selectableCropFields = this.selectableCropFields.map((cropField) => {
      if (cropField.cropName === cropName) {
        cropField.isSelected = !isSelected;
      }
      return cropField;
    });
  }

  allCropFieldsOfCropTypeSelected = (cropName: string, cropFieldsOfCropTypeCount: number): number =>
    this.selectableCropFields.filter(
      (cropField) => cropField.cropName === cropName && cropField.isSelected,
    ).length - cropFieldsOfCropTypeCount;
}
